/**
 * Hook for infinite scroll search results.
 * Fetches documents from Typesense based on current filters.
 */

import { useMemo } from 'react';

import type { GridSortModel } from '@mui/x-data-grid-pro';
import {
  keepPreviousData,
  useInfiniteQuery,
  type InfiniteData,
} from '@tanstack/react-query';

import { useTypesenseAPIKey } from './useTypesenseAPIKey';
import { typesense } from '@/api/typesense/typesenseClient';
import type { FilterSet } from '@/lib/filter-dsl';
import type { ResourceConfig } from '@/lib/resource-config';
import { toFilterBy, toFacetBy, toQueryBy } from '@/lib/typesense-map';

// Column configuration for custom sort field mapping
export interface ColumnSortConfig {
  field: string;
  sortField?: string; // Optional field to sort by instead of the column field
}

interface UseSearchResultsParams {
  config: ResourceConfig;
  filterSet: FilterSet;
  perPage?: number;
  sortModel?: GridSortModel;
  sortFieldMappings?: ColumnSortConfig[]; // Maps display fields to their Typesense sort fields
  query?: string;
}

interface SearchResultsPage<T> {
  documents: T[];
  page: number;
  totalPages: number;
  totalResults: number;
}

const DEFAULT_PER_PAGE = 50;

/**
 * Fetches search results with infinite scroll support.
 * Automatically resets to page 1 when filterSet changes.
 *
 * @param params - Hook parameters
 * @returns React Query infinite query result
 */
export function useSearchResults<T>({
  config,
  filterSet,
  perPage = DEFAULT_PER_PAGE,
  sortModel,
  sortFieldMappings,
  query,
}: UseSearchResultsParams) {
  const { apiKey } = useTypesenseAPIKey(config.typesenseCollection);

  // Normalize empty string queries to wildcard, same as useFacetCounts
  const effectiveQuery = query === '' ? '*' : (query ?? '*');

  const filterBy = toFilterBy(config, filterSet);
  const facetBy = toFacetBy(config);
  const queryBy = toQueryBy(config);

  // Convert MUI GridSortModel to Typesense sort_by format
  const sortBy = useMemo(() => {
    if (!sortModel || sortModel.length === 0) return undefined;
    return sortModel
      .map((item) => {
        // Check if field has a custom sortField mapping defined
        const mapping = sortFieldMappings?.find(
          (col) => col.field === item.field,
        );
        const field = mapping?.sortField ?? item.field;
        return `${field}:${item.sort}`;
      })
      .join(',');
  }, [sortModel, sortFieldMappings]);

  const queryKey = [
    'searchResults',
    config.resourceKey,
    config.typesenseCollection,
    filterBy,
    sortBy,
    effectiveQuery,
    !!apiKey,
  ];

  return useInfiniteQuery<
    SearchResultsPage<T>,
    Error,
    InfiniteData<SearchResultsPage<T>>,
    readonly unknown[],
    number
  >({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const page = pageParam ?? 1;

      if (!apiKey) {
        console.error('[useSearchResults] API key not available');
        return {
          documents: [],
          page,
          totalPages: 0,
          totalResults: 0,
        };
      }

      try {
        const searchRequest = {
          searches: [
            {
              collection: config.typesenseCollection,
              'x-typesense-api-key': apiKey,
              q: effectiveQuery,
              query_by: queryBy,
              ...(filterBy && { filter_by: filterBy }),
              ...(facetBy.length > 0 && { facet_by: facetBy.join(',') }),
              ...(sortBy && { sort_by: sortBy }),
              per_page: perPage,
              page,
            },
          ],
        };

        const multiSearchResults =
          await typesense.multiSearch.perform(searchRequest);
        const result = multiSearchResults.results[0] as {
          hits?: Array<{ document: unknown }>;
          found?: number;
        };

        const documents = (result.hits || []).map((hit) => hit.document as T);
        const totalResults = result.found || 0;
        const totalPages = Math.ceil(totalResults / perPage);

        return {
          documents,
          page,
          totalPages,
          totalResults,
        };
      } catch (error) {
        console.error('[useSearchResults] Error fetching results:', error);
        return {
          documents: [],
          page,
          totalPages: 0,
          totalResults: 0,
        };
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 60 * 1000, // 1 minute
    enabled: !!apiKey,
    placeholderData: keepPreviousData,
  });
}
